// Records one anonymous summary per full-screen photograph presentation for the photo insights report. The tracker
// is pure (time, identity and transport are injected) so its state machine can be tested without a browser, and every
// public method swallows its own failures: telemetry must never throw into the gallery code.

export const photoPresentationEndpoint = '/api/telemetry/photo-presentation';
export const maximumPresentationMilliseconds = 30 * 60 * 1000;
/** Presentations cancelled before they were fully shown are dropped when they lasted less than this. */
export const minimumUnshownPresentationMilliseconds = 250;

export type PhotoPresentationSource = 'auto' | 'user';
export type PhotoPresentationTrigger = 'auto' | 'tile' | 'link' | 'arrow' | 'history';
export type PhotoPresentationEndReason = 'closed' | 'timed-out' | 'navigated' | 'ordered' | 'page-hidden';
export type PhotoPresentationFlag = 'zoomed' | 'orderClicked' | 'linkCopied' | 'interacted';

export type PhotoPresentationRecord = {
    browserId: string;
    sessionId: string;
    imageKey: string;
    source: PhotoPresentationSource;
    trigger: PhotoPresentationTrigger;
    durationMs: number;
    endReason: PhotoPresentationEndReason;
    zoomed: boolean;
    orderClicked: boolean;
    linkCopied: boolean;
    interacted: boolean;
};

export type PhotoInsightsIdentity = {
    browserId: string;
    sessionId: string;
};

export type PhotoInsightsDependencies = {
    /** A monotonic clock in milliseconds, normally performance.now(). */
    now: () => number;
    getIdentity: () => PhotoInsightsIdentity;
    send: (record: PhotoPresentationRecord) => void;
};

/** One open presentation. Every method is a no-op once the presentation has ended. */
export type PhotoPresentation = {
    readonly imageKey: string;
    readonly isEnded: boolean;
    /** Notes that the photograph finished appearing (the expand or slide animation completed). */
    shown: () => void;
    /** Sets a flag once; every flag also counts as an interaction. */
    mark: (flag: PhotoPresentationFlag) => void;
    /** Sends the record once, unless it was a very short presentation cancelled before it was shown. */
    end: (reason: PhotoPresentationEndReason) => void;
};

export type PhotoInsightsTracker = {
    begin: (imageKey: string, source: PhotoPresentationSource, trigger: PhotoPresentationTrigger) => PhotoPresentation;
    /** Ends every open presentation, for pagehide. */
    endAll: (reason: PhotoPresentationEndReason) => void;
    readonly openCount: number;
};

const endedPresentation: PhotoPresentation = Object.freeze({
    imageKey: '',
    isEnded: true,
    shown: () => undefined,
    mark: () => undefined,
    end: () => undefined
});

/** Rounds a measured duration and clamps it to between 0 and 30 minutes. */
export function clampPresentationDuration(milliseconds: number): number {
    if (!Number.isFinite(milliseconds) || milliseconds <= 0) return 0;
    return Math.min(Math.round(milliseconds), maximumPresentationMilliseconds);
}

export function createPhotoInsightsTracker(dependencies: PhotoInsightsDependencies): PhotoInsightsTracker {
    const open = new Set<PhotoPresentation>();

    function begin(
        imageKey: string,
        source: PhotoPresentationSource,
        trigger: PhotoPresentationTrigger
    ): PhotoPresentation {
        try {
            const startedAt = dependencies.now();
            const flags = { zoomed: false, orderClicked: false, linkCopied: false, interacted: false };
            let wasShown = false;
            let ended = false;
            const presentation: PhotoPresentation = {
                imageKey,
                get isEnded() {
                    return ended;
                },
                shown() {
                    if (!ended) wasShown = true;
                },
                mark(flag) {
                    if (ended) return;
                    flags[flag] = true;
                    flags.interacted = true;
                },
                end(reason) {
                    if (ended) return;
                    ended = true;
                    open.delete(presentation);
                    try {
                        const elapsed = dependencies.now() - startedAt;
                        if (!wasShown && !(elapsed >= minimumUnshownPresentationMilliseconds)) return;
                        const identity = dependencies.getIdentity();
                        dependencies.send({
                            browserId: identity.browserId,
                            sessionId: identity.sessionId,
                            imageKey,
                            source,
                            trigger,
                            durationMs: clampPresentationDuration(elapsed),
                            endReason: reason,
                            ...flags
                        });
                    } catch {
                        // Telemetry must never interfere with the public site.
                    }
                }
            };
            open.add(presentation);
            return presentation;
        } catch {
            return endedPresentation;
        }
    }

    return {
        begin,
        endAll(reason) {
            for (const presentation of [...open]) presentation.end(reason);
        },
        get openCount() {
            return open.size;
        }
    };
}

/** Posts one presentation record without waiting, surviving page unload. */
export function sendPhotoPresentation(record: PhotoPresentationRecord): void {
    try {
        void fetch(photoPresentationEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            keepalive: true,
            body: JSON.stringify(record)
        }).catch(() => {
            // Telemetry must never interfere with the public site.
        });
    } catch {
        // fetch can throw synchronously, for example when the keepalive quota is exhausted.
    }
}
